/**
 * TaskReport.js – Tageserledigungsliste mit Zeitraum-Filter (Punkt 6 + 8)
 * Zeigt: pro Aufgabe wer sie wann erledigt hat oder ob offen geblieben.
 * Auto-completed (Temperaturkopplung) werden mit Hinweis gekennzeichnet.
 */
import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CheckCircle2, XCircle, Clock, Download, FileDown,
  Loader2, Filter, Zap,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_COLORS = {
  kritisch: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  hoch:     'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  mittel:   'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  niedrig:  'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

const fmt = isoStr => {
  if (!isoStr) return '–';
  try {
    return new Date(isoStr).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return '–'; }
};

const fmtDate = iso => {
  if (!iso) return '–';
  const [y, m, d] = iso.split('-');
  return `${d}.${m}.${y}`;
};

export const TaskReport = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(today);
  const [dateTo, setDateTo]     = useState(today);
  const [locationId, setLocationId] = useState('');
  const [locations, setLocations]   = useState([]);
  const [rows, setRows]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [exporting, setExporting]   = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    axios.get(`${API}/api/admin/locations`, { headers })
      .then(r => setLocations(r.data || []))
      .catch(() => {});
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (locationId) params.set('location_id', locationId);
      const r = await axios.get(`${API}/api/tasks/report?${params}`, { headers });
      setRows(r.data || []);
    } catch { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ date_from: dateFrom, date_to: dateTo });
      if (locationId) params.set('location_id', locationId);
      const r = await axios.get(`${API}/api/tasks/report/pdf?${params}`,
        { headers, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `Aufgaben_${dateFrom.replace(/-/g, '')}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('PDF-Export fehlgeschlagen'); }
    finally { setExporting(false); }
  };

  const total    = rows.length;
  const done     = rows.filter(r => r.is_completed).length;
  const open     = total - done;
  const autoDone = rows.filter(r => r.auto_completed).length;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl mx-auto">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold">Aufgaben-Protokoll</h1>
            <p className="text-muted-foreground text-sm mt-0.5">Wer hat was wann erledigt – oder offen gelassen.</p>
          </div>
          <Button onClick={handleExportPDF} disabled={exporting || !rows.length} variant="outline" className="gap-2">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            PDF exportieren
          </Button>
        </div>

        {/* Filter */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-medium">Von</label>
                <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-medium">Bis</label>
                <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground font-medium">Standort</label>
                <select value={locationId} onChange={e => setLocationId(e.target.value)}
                  className="h-9 rounded-md border bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary min-w-[160px]">
                  <option value="">Alle Standorte</option>
                  {locations.map(l => (
                    <option key={l.id} value={l.id}>{l.name}</option>
                  ))}
                </select>
              </div>
              <Button onClick={fetchReport} disabled={loading} className="gap-2 mt-auto">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Filter className="h-4 w-4" />}
                Anzeigen
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Zusammenfassung */}
        {!loading && rows.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Gesamt', value: total, color: 'text-foreground' },
              { label: 'Erledigt', value: done, color: 'text-green-600' },
              { label: 'Offen', value: open, color: open > 0 ? 'text-red-600' : 'text-muted-foreground' },
              { label: 'Auto-erledigt', value: autoDone, color: 'text-blue-600', title: 'Via Temperaturkontrolle' },
            ].map(s => (
              <div key={s.label} className="bg-muted/30 rounded-xl p-3 text-center" title={s.title}>
                <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Tabelle */}
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Lade...
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <CheckCircle2 className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>Keine Daten für diesen Zeitraum.</p>
          </div>
        ) : (
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-muted/30">
                    <th className="text-left px-4 py-2.5 font-semibold text-xs text-muted-foreground">Aufgabe</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-xs text-muted-foreground">Datum</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-xs text-muted-foreground">Ziel</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-xs text-muted-foreground">Status</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-xs text-muted-foreground">Erledigt von</th>
                    <th className="text-left px-3 py-2.5 font-semibold text-xs text-muted-foreground">Uhrzeit</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b last:border-0 ${i % 2 === 0 ? '' : 'bg-muted/20'}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2">
                          <span>{r.name}</span>
                          {r.auto_completed && (
                            <span title="Automatisch via Temperaturkontrolle erledigt"
                              className="inline-flex items-center gap-1 text-xs bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 px-1.5 py-0.5 rounded">
                              <Zap className="h-3 w-3" /> Auto
                            </span>
                          )}
                          <span className={`text-xs px-1.5 py-0.5 rounded ${PRIORITY_COLORS[r.priority] || ''}`}>
                            {r.priority}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{fmtDate(r.turnus_date)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {r.ziel_uhrzeit || '–'}
                        </span>
                      </td>
                      <td className="px-3 py-2.5">
                        {r.is_completed ? (
                          <span className="flex items-center gap-1 text-green-600 font-medium">
                            <CheckCircle2 className="h-4 w-4" /> Erledigt
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-600 font-medium">
                            <XCircle className="h-4 w-4" /> Offen
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-muted-foreground">{r.completed_by_name || '–'}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{fmt(r.completed_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default TaskReport;
